import { subscriptionPrices } from "@arbeidsark/shared"
import type { StripeSubscription } from "@arbeidsark/shared"
import { Link } from "react-router-dom"
import styled from "styled-components"
import { Button, OutlineButton } from "~/components"
import { useTranslate } from "~/ur-react-hooks"
import { useUser } from "~/utils"
import { format } from "~/utils/date-fns/format"
import PersonalSubscriptionWillCancel from "./PersonalSubscriptionWillCancel"
import SubscriptionPersonalTrial from "./SubscriptionPersonalTrial"

const Layout = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 6rem;
`

const Card = styled.div`
  background: white;
  box-shadow: ${(props) => props.theme.shadow.elevation3};
  padding: 2rem;
  width: 456px;
  display: flex;
  flex-direction: column;
  align-items: center;
  border-radius: 8px;
`

const Title = styled.h3`
  margin: 0;
`

const Subtitle = styled.p`
  margin: 0;
  margin-top: 1rem;
`

const ButtonWrapper = styled.div`
  margin-top: 1.375rem;
`

const Cancel = styled.button`
  ${(props) => props.theme.button.reset}
  color: ${(props) => props.theme.colors.gray60};
  margin-top: 1.375rem;

  &:hover {
    text-decoration: underline;
  }
`

const Price = styled.div`
  margin-top: 1rem;
  font-size: 1.5rem;
`

export interface SubscriptionPersonalProps {
  subscription: StripeSubscription
}

const SubscriptionPersonal: React.FC<SubscriptionPersonalProps> = ({
  subscription,
}) => {
  const user = useUser()

  const nextPaymentFormatted = format(
    subscription.nextPayment ?? new Date(),
    "dd.MM.yyyy",
  )
  const translations = useTranslate({
    subscriptionTrialTitle: "subscription-personal.trial-title",
    subscriptionRegularTitle: "subscription-personal.regular-title",
    subscriptionTrialSubtitle: [
      "subscription-personal.trial-subtitle",
      { date: nextPaymentFormatted },
    ],
    subscriptionRegularSubtitle: [
      "subscription-personal.regular-subtitle",
      { date: nextPaymentFormatted },
    ],
    subscriptionWillCancelSubtitle: [
      "subscription-personal.will-cancel-subtitle",
      { date: nextPaymentFormatted },
    ],
    subscriptionTrialButton: "subscription-personal.trial-button",
    subscriptionRegularButton: "subscription-personal.regular-button",
    subscriptionCancel: "subscription-personal.cancel",
    hasPaymentMethodText: [
      "subscription-personal.has-payment-method-text",
      { sum: "299" },
    ],
    price: ["subscription-personal.price", { price: "299" }],
    willCancel: "subscription-personal.will-cancel",
    reEnable: "subscription-personal.reactivate",
    reEnableConfirm: "subscription-personal.reactivate-confirm",
  })

  const isTrialing = subscription.status === "trialing"
  const subscriptionPeriodicity = subscription.periodicity
  const price = subscriptionPrices[subscriptionPeriodicity] / 100
  const showChangePaymentMethodButton = !user.hasActivePaymentMethod
  const willCancel = subscription.cancelsAt !== null

  if (willCancel) {
    return <PersonalSubscriptionWillCancel subscription={subscription} />
  } else if (isTrialing) {
    return <SubscriptionPersonalTrial subscription={subscription} />
  }

  return (
    <Layout>
      <Card>
        <Title>{translations.subscriptionRegularTitle}</Title>
        <Price>{translations.price({ price: price.toString() })}</Price>
        <Subtitle>{translations.subscriptionRegularSubtitle()}</Subtitle>
        <ButtonWrapper>
          <Link to={"/subscription/change-payment-method"}>
            {showChangePaymentMethodButton ? (
              <Button>{translations.subscriptionRegularButton}</Button>
            ) : (
              <OutlineButton>
                {translations.subscriptionRegularButton}
              </OutlineButton>
            )}
          </Link>
        </ButtonWrapper>
        <Link to="/subscription/cancel">
          <Cancel>{translations.subscriptionCancel}</Cancel>
        </Link>
      </Card>
    </Layout>
  )
}

export default SubscriptionPersonal
